import axios from 'axios';

// TMDB API configuration
const TMDB_API_KEY = import.meta.env.VITE_TMDB_API_KEY || 'your-api-key-here';
const TMDB_BASE_URL = 'https://api.themoviedb.org/3';

// Create axios instance with Bearer token authentication
const tmdbApi = axios.create({
  baseURL: TMDB_BASE_URL,
  headers: {
    'Authorization': `Bearer ${TMDB_API_KEY}`,
    'Content-Type': 'application/json',
  },
});

export interface TMDBMovie {
  id: number;
  title: string;
  original_title: string;
  release_date: string;
  overview: string;
  poster_path: string;
  backdrop_path: string;
  vote_average: number;
  vote_count: number;
  genre_ids: number[];
  genres?: { id: number; name: string }[];
  runtime?: number;
  imdb_id?: string;
  homepage?: string;
  production_companies?: { name: string }[];
  director?: string;
}

export interface TMDBMovieSearchResult {
  page: number;
  results: TMDBMovie[];
  total_pages: number;
  total_results: number;
}

export interface TMDBMovieDetail extends TMDBMovie {
  adult: boolean;
  belongs_to_collection: any;
  budget: number;
  origin_country: string[];
  original_language: string;
  popularity: number;
  production_countries: Array<{ iso_3166_1: string; name: string }>;
  revenue: number;
  spoken_languages: Array<{ english_name: string; iso_639_1: string; name: string }>;
  status: string;
  tagline: string;
  video: boolean;
  credits?: {
    cast: Array<{ name: string; character: string; profile_path: string }>;
    crew: Array<{ name: string; job: string; department: string }>;
  };
  videos?: {
    results: Array<{ key: string; name: string; site: string; type: string }>;
  };
  external_ids?: {
    imdb_id: string;
    facebook_id: string;
    instagram_id: string;
    twitter_id: string;
  };
}

// Search for movies by title and year
export const searchMovies = async (title: string, year?: number): Promise<TMDBMovie[]> => {
  try {
    const params: any = {
      query: title,
      include_adult: false,
      language: 'en-US',
    };

    if (year) {
      params.primary_release_year = year;
    }

    const response = await tmdbApi.get<TMDBMovieSearchResult>('/search/movie', { params });
    return response.data.results;
  } catch (error) {
    console.error('Error searching movies:', error);
    return [];
  }
};

// Get movie details by TMDB ID with all append_to_response data
export const getMovieDetails = async (tmdbId: number): Promise<TMDBMovieDetail | null> => {
  try {
    const response = await tmdbApi.get<TMDBMovieDetail>(`/movie/${tmdbId}`, {
      params: {
        append_to_response: 'credits,videos,external_ids',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting movie details:', error);
    return null;
  }
};

// Get movie details by IMDB ID
export const getMovieByImdbId = async (imdbId: string): Promise<TMDBMovieDetail | null> => {
  try {
    const response = await tmdbApi.get(`/find/${imdbId}`, {
      params: {
        external_source: 'imdb_id',
        append_to_response: 'credits,videos,external_ids',
      },
    });
    
    if (response.data.movie_results && response.data.movie_results.length > 0) {
      return response.data.movie_results[0];
    }
    return null;
  } catch (error) {
    console.error('Error getting movie by IMDB ID:', error);
    return null;
  }
};

// Get poster URL
export const getPosterUrl = (posterPath: string, size: 'w92' | 'w154' | 'w185' | 'w342' | 'w500' | 'w780' | 'original' = 'w500'): string => {
  if (!posterPath) return '';
  return `https://image.tmdb.org/t/p/${size}${posterPath}`;
};

// Get backdrop URL
export const getBackdropUrl = (backdropPath: string, size: 'w300' | 'w780' | 'w1280' | 'original' = 'w1280'): string => {
  if (!backdropPath) return '';
  return `https://image.tmdb.org/t/p/${size}${backdropPath}`;
};

// Get IMDB URL
export const getImdbUrl = (imdbId: string): string => {
  return `https://www.imdb.com/title/${imdbId}/`;
};

// Get TMDB URL
export const getTmdbUrl = (tmdbId: number): string => {
  return `https://www.themoviedb.org/movie/${tmdbId}`;
};
